/** XeAgents.ai - Tax Rate Management */
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Plus, Loader2, Pencil, Trash2, Percent, Globe, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface TaxRate {
  id: string;
  countryCode: string;
  stateCode: string | null;
  taxType: string;
  rate: string;
  name: string;
  appliesToSubscriptions: boolean;
  appliesToCredits: boolean;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdAt: string;
}

interface TaxRateFormData {
  countryCode: string;
  stateCode: string;
  taxType: string;
  rate: number;
  name: string;
  appliesToSubscriptions: boolean;
  appliesToCredits: boolean;
  isActive: boolean;
}

const TAX_TYPES = [
  { value: "vat", label: "VAT (Value Added Tax)" },
  { value: "gst", label: "GST (Goods & Services Tax)" },
  { value: "sales_tax", label: "Sales Tax" },
  { value: "hst", label: "HST (Harmonized Sales Tax)" },
  { value: "pst", label: "PST (Provincial Sales Tax)" },
];

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IN", name: "India" },
  { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" },
  { code: "NL", name: "Netherlands" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "BE", name: "Belgium" },
  { code: "AT", name: "Austria" },
  { code: "IE", name: "Ireland" },
  { code: "PT", name: "Portugal" },
  { code: "SE", name: "Sweden" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "NO", name: "Norway" },
  { code: "PL", name: "Poland" },
  { code: "CZ", name: "Czech Republic" },
];

export default function TaxRateManagement() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<TaxRate | null>(null);
  const [deletingRate, setDeletingRate] = useState<TaxRate | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCountry, setFilterCountry] = useState<string>("all");

  const [formData, setFormData] = useState<TaxRateFormData>({
    countryCode: "US",
    stateCode: "",
    taxType: "sales_tax",
    rate: 0,
    name: "",
    appliesToSubscriptions: true,
    appliesToCredits: true,
    isActive: true,
  });

  const { data: taxRates, isLoading } = useQuery<TaxRate[]>({
    queryKey: ["/api/admin/tax-rates"],
  });

  const filteredRates = (taxRates || []).filter((rate) => {
    const matchesSearch =
      rate.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rate.countryCode.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCountry = filterCountry === "all" || rate.countryCode === filterCountry;
    return matchesSearch && matchesCountry;
  });

  const createRate = useMutation({
    mutationFn: async (data: TaxRateFormData) => {
      return apiRequest("POST", "/api/admin/tax-rates", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tax-rates"] });
      toast({ title: "Tax Rate Created", description: "The tax rate has been created successfully" });
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Failed to Create Tax Rate", description: error.message, variant: "destructive" });
    },
  });

  const updateRate = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TaxRateFormData> }) => {
      return apiRequest("PATCH", `/api/admin/tax-rates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tax-rates"] });
      toast({ title: "Tax Rate Updated", description: "The tax rate has been updated successfully" });
      setIsEditDialogOpen(false);
      setEditingRate(null);
    },
    onError: (error: any) => {
      toast({ title: "Failed to Update Tax Rate", description: error.message, variant: "destructive" });
    },
  });

  const deleteRate = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/tax-rates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tax-rates"] });
      toast({ title: "Tax Rate Deleted", description: "The tax rate has been deleted successfully" });
      setIsDeleteDialogOpen(false);
      setDeletingRate(null);
    },
    onError: (error: any) => {
      toast({ title: "Failed to Delete Tax Rate", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({
      countryCode: "US",
      stateCode: "",
      taxType: "sales_tax",
      rate: 0,
      name: "",
      appliesToSubscriptions: true,
      appliesToCredits: true,
      isActive: true,
    });
  };

  const openEditDialog = (rate: TaxRate) => {
    setEditingRate(rate);
    setFormData({
      countryCode: rate.countryCode,
      stateCode: rate.stateCode || "",
      taxType: rate.taxType,
      rate: parseFloat(rate.rate) * 100,
      name: rate.name,
      appliesToSubscriptions: rate.appliesToSubscriptions,
      appliesToCredits: rate.appliesToCredits,
      isActive: rate.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const getCountryName = (code: string) => {
    return COUNTRIES.find((c) => c.code === code)?.name || code;
  };

  const getTaxTypeLabel = (type: string) => {
    return TAX_TYPES.find((t) => t.value === type)?.label || type;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5" />
                Tax Rate Management
              </CardTitle>
              <CardDescription>
                Configure tax rates for different countries and regions. These rates are applied to invoices.
              </CardDescription>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Tax Rate
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tax rates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterCountry} onValueChange={setFilterCountry}>
              <SelectTrigger className="w-[200px]">
                <Globe className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {COUNTRIES.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tax Rates Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Country</TableHead>
                  <TableHead>State/Region</TableHead>
                  <TableHead>Tax Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead>Applies To</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No tax rates found. Click "Add Tax Rate" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRates.map((rate) => (
                    <TableRow key={rate.id}>
                      <TableCell className="font-medium">{getCountryName(rate.countryCode)}</TableCell>
                      <TableCell>{rate.stateCode || "-"}</TableCell>
                      <TableCell>{getTaxTypeLabel(rate.taxType)}</TableCell>
                      <TableCell>{rate.name}</TableCell>
                      <TableCell className="text-right font-mono">
                        {(parseFloat(rate.rate) * 100).toFixed(2)}%
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {rate.appliesToSubscriptions && (
                            <Badge variant="outline" className="text-xs">Subs</Badge>
                          )}
                          {rate.appliesToCredits && (
                            <Badge variant="outline" className="text-xs">Credits</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={rate.isActive ? "default" : "secondary"}>
                          {rate.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(rate)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setDeletingRate(rate);
                              setIsDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Tax Rate</DialogTitle>
            <DialogDescription>Create a new tax rate for a country or region.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country</Label>
                <Select
                  value={formData.countryCode}
                  onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>State/Region Code (Optional)</Label>
                <Input
                  value={formData.stateCode}
                  onChange={(e) => setFormData({ ...formData, stateCode: e.target.value })}
                  placeholder="e.g., CA, NY"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tax Type</Label>
              <Select
                value={formData.taxType}
                onValueChange={(value) => setFormData({ ...formData, taxType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Standard VAT"
                />
              </div>
              <div className="space-y-2">
                <Label>Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })}
                  placeholder="e.g., 20"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Applies to Subscriptions</Label>
                <Switch
                  checked={formData.appliesToSubscriptions}
                  onCheckedChange={(checked) => setFormData({ ...formData, appliesToSubscriptions: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Applies to Credit Purchases</Label>
                <Switch
                  checked={formData.appliesToCredits}
                  onCheckedChange={(checked) => setFormData({ ...formData, appliesToCredits: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createRate.mutate({ ...formData, rate: formData.rate / 100 })}
              disabled={createRate.isPending || !formData.name || formData.rate <= 0}
            >
              {createRate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Tax Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Tax Rate</DialogTitle>
            <DialogDescription>Update the tax rate configuration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country</Label>
                <Select
                  value={formData.countryCode}
                  onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>State/Region Code</Label>
                <Input
                  value={formData.stateCode}
                  onChange={(e) => setFormData({ ...formData, stateCode: e.target.value })}
                  placeholder="e.g., CA, NY"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tax Type</Label>
              <Select
                value={formData.taxType}
                onValueChange={(value) => setFormData({ ...formData, taxType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Applies to Subscriptions</Label>
                <Switch
                  checked={formData.appliesToSubscriptions}
                  onCheckedChange={(checked) => setFormData({ ...formData, appliesToSubscriptions: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Applies to Credit Purchases</Label>
                <Switch
                  checked={formData.appliesToCredits}
                  onCheckedChange={(checked) => setFormData({ ...formData, appliesToCredits: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                editingRate &&
                updateRate.mutate({
                  id: editingRate.id,
                  data: { ...formData, rate: formData.rate / 100 },
                })
              }
              disabled={updateRate.isPending}
            >
              {updateRate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Tax Rate</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the tax rate "{deletingRate?.name}" for{" "}
              {deletingRate && getCountryName(deletingRate.countryCode)}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingRate && deleteRate.mutate(deletingRate.id)}
              disabled={deleteRate.isPending}
            >
              {deleteRate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
